import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Table from "components/DesignSystem/Table/V2/Table";
import { SettingsAccordion } from "pages/Books/Settings/SettingsAccordion";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";

import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { CONNECTION_TYPE } from "dictionaries";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import { EmptyScreen } from "pages/Books/EmptyScreen";
import { useMemo } from "react";
import {
  ColumnDef,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import Bank from "static/images/Bank.svg";
import NoTransaction from "static/images/NoTransaction.svg";
import ThreeDots from "static/images/ThreeDots.svg";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import { Banks } from "types/Models/banks";
import {
  Route,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import { Divider } from "components/design/Divider";
import { ReconcileButton } from "./ReconcileButton";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { pluralize } from "utils/pluralize";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { isStripeBank } from "utils/bankNameAcc";

const BrandAccordionItem = ({
  brand,
  columns,
}: {
  brand: {
    brandName: string;
    logo: string;
    accounts: Banks[];
    activeAccountCount: number;
  };
  columns: ColumnDef<Banks, any>[];
}) => {
  const table = useReactTable({
    data: brand.accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <SettingsAccordion.Item value={brand.brandName}>
      <SettingsAccordion.Trigger>
        <div className="t-flex t-items-center t-gap-3 t-flex-1">
          <img
            src={brand.logo || Bank}
            alt={brand.brandName}
            className="t-w-8 t-h-8 t-rounded-full"
          />
          <span className="t-text-text-60 t-text-body-sm">
            {brand.brandName}
          </span>
        </div>
        <span className="t-text-text-30 t-text-body-sm-italic t-mr-2 t-italic">
          {pluralize(
            brand.activeAccountCount,
            "active account",
            "active accounts"
          )}
        </span>
      </SettingsAccordion.Trigger>
      <SettingsAccordion.Content className="t-ml-7 t-mb-2">
        <Table.Container>
          <Table.Content className="sm:t-min-w-[720px]">
            <Table.Head className="!t-bg-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.HeadCell
                      key={header.id}
                      style={{ width: header.getSize() }}
                      isUpperCase={false}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.HeadCell>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body>
              {table.getRowModel().rows.map((row, rowIndex, rows) => {
                const isLastRow = rowIndex === rows.length - 1;

                return (
                  <Table.Row
                    key={row.id}
                    className={isLastRow ? "!t-border-b-0" : ""}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Content>
        </Table.Container>
      </SettingsAccordion.Content>
    </SettingsAccordion.Item>
  );
};

export const Reconciliation = () => {
  usePageTitle("Reconciliation");
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const {
    data: ledger,
    isLoading,
    isSuccess,
  } = useGetEntityBanksQuery(
    { entityId, groupId },
    { skip: !entityId || !groupId }
  );

  const history = useHistory();
  const { search } = useLocation();
  const { url } = useRouteMatch();

  const { accounts = [] } = ledger || {};

  const accountsByBrand = useMemo(() => {
    const grouped = accounts.reduce((acc, account) => {
      const brandName = account.bank_brand.name;
      if (!acc[brandName]) {
        acc[brandName] = {
          logo: account.bank_brand.logo,
          accounts: [],
        };
      }
      acc[brandName].accounts.push(account);
      return acc;
    }, {} as Record<string, { logo: string; accounts: Banks[] }>);

    return Object.entries(grouped).map(([brandName, data]) => ({
      brandName,
      logo: data.logo,
      accounts: data.accounts,
      activeAccountCount: data.accounts.length,
    }));
  }, [accounts]);

  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );
  const columnHelper = createColumnHelper<Banks>();

  const columns = [
    columnHelper.accessor((row) => row, {
      id: "Account",
      cell: (info) => {
        const {
          account: { nickname, mask, connection_type },
          bank_brand: { logo, name },
        } = info.getValue();

        return (
          <div className="t-flex t-items-center t-gap-2">
            <img
              src={logo || Bank}
              alt={name}
              className="t-w-8 t-rounded-full"
            />
            <div className="t-text-text-100">
              <div className="t-text-subtext">{nickname}</div>
              {mask && (
                <div className="t-text-body-sm">
                  {`\u2022\u2022 ${mask}`}
                  <span>{CONNECTION_TYPE[connection_type]}</span>
                </div>
              )}
            </div>
          </div>
        );
      },
      header: () => <HeadCellContent title="Account" />,
    }),
    columnHelper.accessor((row) => row, {
      id: "Last reconciled",
      cell: (info) => {
        const {
          account: { last_reconciled_date },
        } = info.getValue();

        if (!last_reconciled_date) {
          return (
            <Tag tagType="gray" size="small">
              Not started
            </Tag>
          );
        }

        return (
          <div className="t-text-text-60 t-text-body-sm">
            {dayjs(last_reconciled_date).format(DISPLAY_DATE)}
          </div>
        );
      },
      header: () => <HeadCellContent title="Last reconciled till" />,
    }),
    columnHelper.accessor((row) => row, {
      id: "Last reconciled balance",
      cell: (info) => {
        const {
          account: { last_reconciled_balance, currency: accountCurrency },
        } = info.getValue();

        if (!last_reconciled_balance) {
          return <div className="t-text-text-60 t-text-body-sm">-</div>;
        }

        return (
          <div className="t-text-body-sm t-text-text-60">
            <AmountSuperScript
              amount={Number(last_reconciled_balance)}
              currencyCode={accountCurrency}
            />
          </div>
        );
      },
      header: () => <HeadCellContent title="Last reconciled balance" />,
    }),
    columnHelper.accessor("account.last_reconcilation_status", {
      id: "Action",
      size: 15,
      cell: (info) => {
        const {
          row: { original },
        } = info;
        const { last_reconciled_date, reconcilation_start_date, uuid } =
          original.account;

        return (
          <ReconcileButton
            bankAccountId={uuid}
            entityId={entityId}
            lastReconciledDate={last_reconciled_date}
            reconciliationStartDate={reconcilation_start_date}
            parentPage="all"
            hasWritePermission={hasWritePermission}
            isStripe={isStripeBank(original)}
          />
        );
      },
      header: () => <HeadCellContent title="Action" />,
    }),
    columnHelper.accessor((row) => row, {
      id: "Menu",
      size: 5,
      cell: (info) => {
        const {
          account: { uuid },
        } = info.getValue();

        return (
          <Dropdown.Root>
            <Dropdown.Trigger asChild>
              <div>
                <Button size="xsmall" customType="ghost_icon">
                  <img src={ThreeDots} alt="Menu" className="t-select-none" />
                </Button>
              </div>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content sideOffset={8} side="bottom" align="start">
                <Dropdown.Item
                  onSelect={() =>
                    history.push(`${url}/history/${uuid}${search}`)
                  }
                >
                  History
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        );
      },
      header: () => null,
    }),
  ];

  const isEmpty = accounts.length === 0;

  const accordionDefaultValue = Boolean(
    accountsByBrand && accountsByBrand.length > 0
  )
    ? [accountsByBrand[0].brandName]
    : accountsByBrand.map((brand) => brand.brandName);

  return (
    <>
      <Async.Root {...{ isLoading, isEmpty, isSuccess }}>
        <Async.Empty>
          <EmptyScreen
            text="Please connect your US bank account, so that we can sync transcations"
            className="!t-h-[75dvh]"
          >
            <img src={NoTransaction} alt="No Transaction" />
          </EmptyScreen>
        </Async.Empty>
        <Async.Success>
          <div className="t-flex t-flex-col t-gap-6 t-pb-20">
            <SettingsAccordion.Root defaultValue={accordionDefaultValue}>
              {accountsByBrand.map((brand, index) => (
                <div key={brand.brandName}>
                  <BrandAccordionItem brand={brand} columns={columns} />
                  {index !== accountsByBrand.length - 1 && (
                    <Divider color="light-grey" />
                  )}
                </div>
              ))}
            </SettingsAccordion.Root>
          </div>
        </Async.Success>
      </Async.Root>
    </>
  );
};
